/**
 * Audio Library Utilities
 *
 * Utilities for managing recently used audio tracks in localStorage.
 * Co-located with ClipAssets for easy access to audio-related functionality.
 */

import { findMusicByUrl } from '../constants';

const STORAGE_KEY = 'content-studio:recently-used-audio';
const MAX_RECENT_TRACKS = 5;

/**
 * Get the list of recently used music track IDs.
 * Returns an array of track IDs in order of most recent first.
 */
export const getRecentlyUsed = (): string[] => {
  try {
    const stored = localStorage.getItem(STORAGE_KEY);
    if (!stored) {
      return [];
    }
    const parsed = JSON.parse(stored);
    return Array.isArray(parsed) ? parsed : [];
  } catch (error) {
    console.error('Error reading recently used audio from localStorage:', error);
    return [];
  }
};

/**
 * Add a music track ID to the recently used list.
 * The track will be added to the front of the list.
 * If the track already exists, it will be moved to the front.
 * Enforces a maximum of MAX_RECENT_TRACKS items.
 *
 * @param musicId - The ID of the music track to add
 */
export const addToRecentlyUsed = (musicId: string): void => {
  try {
    if (!musicId) {
      return;
    }

    const current = getRecentlyUsed();

    // Remove the track if it already exists (we'll add it to the front)
    const filtered = current.filter(id => id !== musicId);

    // Add the new track to the front
    const updated = [musicId, ...filtered];

    // Limit to MAX_RECENT_TRACKS
    const limited = updated.slice(0, MAX_RECENT_TRACKS);

    localStorage.setItem(STORAGE_KEY, JSON.stringify(limited));
  } catch (error) {
    console.error('Error saving recently used audio to localStorage:', error);
  }
};

/**
 * Clear all recently used tracks from localStorage.
 * Useful for testing or user preference to clear history.
 */
export const clearRecentlyUsed = (): void => {
  try {
    localStorage.removeItem(STORAGE_KEY);
  } catch (error) {
    console.error('Error clearing recently used audio from localStorage:', error);
  }
};

/**
 * Get the display name for an audio element.
 * Prioritizes library track names, then falls back to provided name or formatted filename.
 */
export const getAudioDisplayName = ({ url, name }: { url: string; name?: string }): string => {
  const libraryTrack = findMusicByUrl(url);
  if (libraryTrack) {
    return libraryTrack.name;
  }

  if (name?.trim()) {
    return name;
  }

  return url.split('/').pop() || '';
};
